import {
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useClient, useExtractionAccuracy } from '../services/conversionsApi';
import { formatLocalDateTime } from '../lib/provenance';
import type { ClientAccuracy, StreakDot } from '../types';
import { usePageStyles } from '../components/layout/pageStyles';
import GradeLedgerSection from '../components/GradeLedgerSection';
import { badgeWords } from '../lib/accuracyBadge';

const useStyles = makeStyles({
  stats: { display: 'flex', gap: tokens.spacingHorizontalXXL, flexWrap: 'wrap' },
  stat: { display: 'flex', flexDirection: 'column' },
  statValue: { fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  sectionTitle: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold },
  dots: { display: 'flex', gap: tokens.spacingHorizontalXXS, alignItems: 'center' },
  dot: { width: '10px', height: '10px', borderRadius: tokens.borderRadiusCircular, display: 'inline-block' },
  hit: { backgroundColor: tokens.colorPaletteGreenBackground3 },
  miss: { backgroundColor: tokens.colorPaletteRedBackground3 },
});

/** `Dr15ExtractionResult.Method` → its words under `accuracyPage.methods`. */
const METHOD_KEYS: Record<string, string> = {
  'pdfpig-regex': 'textLayer',
  'pdfpig-regex-partial': 'textLayerPartial',
  'pdfpig+gpt-4o': 'textAi',
  'document-intelligence+gpt-4o': 'scanAi',
};

/**
 * Mission item 4 (docs/graded-extraction.md): how well the DR-15 reader matched the journals people exported. It says
 * NO DATA until a DR-15 has been extracted AND its journal exported: never "0%", which would be a claim about an
 * extractor nobody has measured. Read-only.
 *
 * <p>Accuracy per client (docs/accuracy-per-client.md §3): with the header client it shows that client's figures and
 * row; with none, the fleet summary above every client's row. A client with no graded pair is "not graded", never 0.</p>
 */
export default function ExtractionAccuracyPage() {
  const styles = useStyles();
  const frame = usePageStyles();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';
  // Christian's walk (2026-10-28): the page DEFAULTS to the header client; `?view=all` is the all-clients overview, the
  // same columns, one button away. Pushed, so Back returns to the client's own view.
  const viewAll = !!clientId && searchParams.get('view') === 'all';
  const report = useExtractionAccuracy(viewAll ? '' : clientId);
  const client = useClient(clientId);
  const setViewAll = (all: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (all) next.set('view', 'all');
    else next.delete('view');
    setSearchParams(next);
  };
  const pct = (v: number | null | undefined) =>
    v == null ? '—' : new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(v);
  const money = (v: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  const figureLabel = (f: string) => t(`journalPage.dr15.${f}`, { defaultValue: f });
  // The reader's own codes (`Dr15Extractor`) in words (the polish walk of deploy #21 found `pdfpig-regex` on screen); a
  // code added later reads as itself until it has words, never as a raw key.
  const methodLabel = (m: string) => (METHOD_KEYS[m] ? t(`accuracyPage.methods.${METHOD_KEYS[m]}`) : m);

  return (
    <div className={frame.page}>
      <div className={frame.header}>
        <Text as="h1" className={frame.title}>{t('accuracyPage.title')}</Text>
        <Text className={frame.subtitle}>{t('accuracyPage.subtitle')}</Text>
      </div>

      {clientId && (
        <div>
          {viewAll ? (
            <Button onClick={() => setViewAll(false)}>
              {t('accuracyPage.byClient.onlyClient', { name: client.data?.name ?? t('accuracyPage.byClient.thisClient') })}
            </Button>
          ) : (
            <Button onClick={() => setViewAll(true)}>{t('accuracyPage.byClient.allClients')}</Button>
          )}
        </div>
      )}

      {report.isLoading && <Spinner label={t('common.loading')} />}
      {report.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{report.error instanceof Error ? report.error.message : t('common.unknownError')}</MessageBarBody>
        </MessageBar>
      )}

      {report.data?.verdict === 'NO DATA' && (
        <MessageBar intent="info">
          <MessageBarBody>{t('accuracyPage.noData')}</MessageBarBody>
        </MessageBar>
      )}

      {report.data?.verdict === 'graded' && (
        <>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <Text className={styles.statValue}>{report.data.pairs}</Text>
              <Text className={styles.muted}>{t('accuracyPage.pairs')}</Text>
            </div>
            <div className={styles.stat}>
              <Text className={styles.statValue}>{pct(report.data.allFiguresExactPct)}</Text>
              <Text className={styles.muted}>{t('accuracyPage.allExact')}</Text>
            </div>
            <div className={styles.stat}>
              <Text className={styles.statValue}>{pct(report.data.totalDueWithinTolerancePct)}</Text>
              <Text className={styles.muted}>{t('accuracyPage.totalDueClose')}</Text>
            </div>
          </div>

          <Table size="small" aria-label={t('accuracyPage.byFigure')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('accuracyPage.columns.figure')}</TableHeaderCell>
                <TableHeaderCell>{t('accuracyPage.columns.count')}</TableHeaderCell>
                <TableHeaderCell>{t('accuracyPage.columns.exact')}</TableHeaderCell>
                <TableHeaderCell>{t('accuracyPage.columns.close')}</TableHeaderCell>
                <TableHeaderCell>{t('accuracyPage.columns.meanError')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.data.byFigure.map((f) => (
                <TableRow key={f.figure}>
                  <TableCell>{figureLabel(f.figure)}</TableCell>
                  <TableCell>{f.count}</TableCell>
                  <TableCell>{pct(f.exactPct)}</TableCell>
                  <TableCell>{pct(f.withinTolerancePct)}</TableCell>
                  <TableCell>{money(f.meanAbsError)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Table size="small" aria-label={t('accuracyPage.byMethod')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('accuracyPage.columns.method')}</TableHeaderCell>
                <TableHeaderCell>{t('accuracyPage.columns.count')}</TableHeaderCell>
                <TableHeaderCell>{t('accuracyPage.columns.allExact')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.data.byMethod.map((m) => (
                <TableRow key={m.method}>
                  <TableCell>{methodLabel(m.method)}</TableCell>
                  <TableCell>{m.pairs}</TableCell>
                  <TableCell>{pct(m.allFiguresExactPct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {report.data && clientId && !viewAll && report.data.byClient.length === 0 && (
        <MessageBar intent="info">
          <MessageBarBody>{t('accuracyPage.byClient.notGraded')}</MessageBarBody>
        </MessageBar>
      )}

      {report.data && report.data.byClient.length > 0 && (
        <ByClient rows={report.data.byClient} showBadge={report.data.viewerIsAdmin} dependableAfter={report.data.dependableAfter} />
      )}
      {report.data && report.data.byClient.length > 0 && (
        <Text className={styles.muted}>{t('accuracyPage.byClient.note', { n: report.data.dependableAfter })}</Text>
      )}
      {report.data?.viewerIsAdmin && report.data.byClient.length > 0 && (
        <Text className={styles.muted}>{t('accuracyPage.byClient.adminNote')}</Text>
      )}

      {report.data && <Text className={styles.muted}>{t('accuracyPage.notInJournal')}</Text>}

      {/* Accuracy §2: the stored grades, an admin's only (the server sends `ledger` to an admin alone). */}
      {report.data?.viewerIsAdmin && report.data.ledger && <GradeLedgerSection ledger={report.data.ledger} />}
    </div>
  );
}

/**
 * The streak per client × source kind (§1). The Dependable column exists only for an admin (Q4): the server sends no
 * badge to anyone else, and the screen does not rebuild one from the streak.
 */
function ByClient({ rows, showBadge, dependableAfter }: { rows: ClientAccuracy[]; showBadge: boolean; dependableAfter: number }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const when = (dot: StreakDot) => (dot.exportedAt ? formatLocalDateTime(dot.exportedAt, i18n.language) : '—');
  const missName = (dot: StreakDot) => t(`accuracyPage.byClient.missClass.${dot.missClass}`);
  const figureLabel = (f: string) => t(`journalPage.dr15.${f}`, { defaultValue: f });

  return (
    <>
      <Text as="h2" className={styles.sectionTitle}>{t('accuracyPage.byClient.heading')}</Text>
      <Table size="small" aria-label={t('accuracyPage.byClient.tableLabel')}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t('accuracyPage.byClient.columns.client')}</TableHeaderCell>
            <TableHeaderCell>{t('accuracyPage.byClient.columns.source')}</TableHeaderCell>
            <TableHeaderCell>{t('accuracyPage.byClient.columns.streak')}</TableHeaderCell>
            <TableHeaderCell>{t('accuracyPage.byClient.columns.recent')}</TableHeaderCell>
            <TableHeaderCell>{t('accuracyPage.byClient.columns.lastMiss')}</TableHeaderCell>
            {showBadge && <TableHeaderCell>{t('accuracyPage.byClient.columns.badge')}</TableHeaderCell>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.clientId}:${row.sourceKind}`}>
              <TableCell><ClientName clientId={row.clientId} /></TableCell>
              <TableCell>{t(`accuracyPage.byClient.source.${row.sourceKind}`)}</TableCell>
              <TableCell>
                {row.streak}{' '}
                <Text className={styles.muted}>{t('accuracyPage.byClient.gradedOf', { graded: row.graded })}</Text>
              </TableCell>
              <TableCell>
                <span className={styles.dots}>
                  {row.recent.map((dot) => (
                    <span
                      key={dot.journalId}
                      role="img"
                      className={`${styles.dot} ${dot.missClass ? styles.miss : styles.hit}`}
                      aria-label={
                        dot.missClass
                          ? t('accuracyPage.byClient.dotMiss', { miss: missName(dot), date: when(dot) })
                          : t('accuracyPage.byClient.dotExact', { date: when(dot) })
                      }
                      title={
                        dot.missClass
                          ? t('accuracyPage.byClient.dotMiss', { miss: missName(dot), date: when(dot) })
                          : t('accuracyPage.byClient.dotExact', { date: when(dot) })
                      }
                    />
                  ))}
                </span>
              </TableCell>
              <TableCell>
                {!row.lastMiss
                  ? t('accuracyPage.byClient.noMiss')
                  : row.lastMiss.figure
                    ? t('accuracyPage.byClient.lastMissFigure', {
                        miss: missName(row.lastMiss), figure: figureLabel(row.lastMiss.figure), date: when(row.lastMiss),
                      })
                    : t('accuracyPage.byClient.lastMissPlain', { miss: missName(row.lastMiss), date: when(row.lastMiss) })}
              </TableCell>
              {showBadge && (
                <TableCell>
                  {/* In counts, never a bare adjective (Christian, 2026-11-02). */}
                  {row.dependable ? (
                    <Badge appearance="filled" color="success">{t(badgeWords(row, dependableAfter).key, badgeWords(row, dependableAfter).params)}</Badge>
                  ) : (
                    <Text className={styles.muted}>{t(badgeWords(row, dependableAfter).key, badgeWords(row, dependableAfter).params)}</Text>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

/** The client's name from the directory; its id until that answers, so a row is never blank. */
function ClientName({ clientId }: { clientId: string }) {
  const client = useClient(clientId);
  return <>{client.data?.name ?? clientId}</>;
}
